import { DCUIContent, DCUIContentCaptureComplete, DCUITheme } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { ContentPageHeader } from "@containers/common/content-page-header/content-page-header";
import { DCUIThemingPreviewContainer } from "@containers/common/dcui-theming-preview-container/dcui-theming-preview-container";
import { InputFormGroup } from "@containers/common/input-form-group/input-form-group";
import { TextareaFormGroup } from "@containers/common/textarea-form-group/textarea-form-group";
import { ThemingConfigurationContainer } from "@containers/common/theming-configuration-container/theming-configuration-container";
import { ThemingFormContainer } from "@containers/common/theming-form-container/theming-form-container";
import { ThemingSubHeaderContainer } from "@containers/common/theming-sub-header-container/theming-sub-header-container";
import { DCUIThemingTabTypes } from "@enums/dcui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { PreviewEntityTypes } from "@enums/preview-entity-types";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { DCUIThemingStep } from "@idw-types/common/dcui/dcui-theming-step";
import { DCUIThemingCaptureCompleteValidationErrors } from "@idw-types/common/validation/common/dcui-theming-validation-errors";
import { ChangeEvent, useState } from "react";
import { ContentPageBody } from "@containers/common/content-page-body/content-page-body";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type DCUIThemingCompleteTabProps = {
    isDisabled: boolean;
    validationErrors: DCUIThemingCaptureCompleteValidationErrors;
    dcuiTheme: DCUITheme;
    dcuiContent: DCUIContent;
    inheritedDCUIContent: DCUIContent;
    pageLevel: PageLevelTypes;
    documentCaptureSteps: Array<DCUIThemingStep>;
    previewEntityType: PreviewEntityTypes;
    updateDCUIContentPropertyValue: (dcuiContent: DCUIContent) => void;
};

export const DCUIThemingCompleteTabContainer = (
    props: DCUIThemingCompleteTabProps
) => {
    const {
        isDisabled,
        validationErrors,
        dcuiTheme,
        dcuiContent,
        inheritedDCUIContent,
        pageLevel,
        documentCaptureSteps,
        previewEntityType,
        updateDCUIContentPropertyValue,
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);
    const [layout] = useState(LayoutTypes.COLUMN);

    const dcuiThemingResources =
        resources.pages.verificationServiceDetails.tabs.dcuiTheming;

    const updateDCUIContentCaptureCompleteFieldValue = (
        key: keyof DCUIContentCaptureComplete,
        value: string
    ) => {
        const newPropertyValue: DCUIContent = {
            ...dcuiContent,
            captureComplete: {
                ...dcuiContent.captureComplete,
                [key]: value,
            },
        };
        updateDCUIContentPropertyValue(newPropertyValue);
    };

    const handleUploadCompleteTitleChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateDCUIContentCaptureCompleteFieldValue(
            "captureUploadCompleteTitle",
            newValue
        );
    };

    const handleUploadInProgressTitleChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateDCUIContentCaptureCompleteFieldValue("captureUploadTitle", newValue);
    };

    const handleUploadSuccessMessageWithRedirectChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateDCUIContentCaptureCompleteFieldValue(
            "captureSuccessMessage",
            newValue
        );
    };

    const handleUploadSuccessMessageWithoutRedirectChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateDCUIContentCaptureCompleteFieldValue(
            "captureSuccessNoRedirectMessage",
            newValue
        );
    };

    const handleUploadErrorMessageChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        const newValue = event.target.value;
        updateDCUIContentCaptureCompleteFieldValue(
            "captureUploadErrorMessage",
            newValue
        );
    };

    const handleResetUploadCompleteTitle = () => {
        updateDCUIContentCaptureCompleteFieldValue(
            "captureUploadCompleteTitle",
            inheritedDCUIContent.captureComplete.captureUploadCompleteTitle
        );
    };

    const handleResetUploadInProgressTitle = () => {
        updateDCUIContentCaptureCompleteFieldValue(
            "captureUploadTitle",
            inheritedDCUIContent.captureComplete.captureUploadTitle
        );
    };

    const handleResetUploadSuccessMessageWithRedirect = () => {
        updateDCUIContentCaptureCompleteFieldValue(
            "captureSuccessMessage",
            inheritedDCUIContent.captureComplete.captureSuccessMessage
        );
    };

    const handleResetUploadSuccessMessageWithoutRedirect = () => {
        updateDCUIContentCaptureCompleteFieldValue(
            "captureSuccessNoRedirectMessage",
            inheritedDCUIContent.captureComplete.captureSuccessNoRedirectMessage
        );
    };

    const handleResetUploadErrorMessage = () => {
        updateDCUIContentCaptureCompleteFieldValue(
            "captureUploadErrorMessage",
            inheritedDCUIContent.captureComplete.captureUploadErrorMessage
        );
    };

    return (
        <div>
            <ContentPageHeader
                title={dcuiThemingResources.subTabs.captureComplete.title}
            />
            <ContentPageBody isMaxWidth={true}>
                <ThemingSubHeaderContainer
                    description={dcuiThemingResources.subTabs.captureComplete.description}
                />
                <ThemingConfigurationContainer>
                    <ThemingFormContainer>
                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.captureComplete.captureUploadCompleteTitle}
                            currentThemingPropertyValue={dcuiContent.captureComplete.captureUploadCompleteTitle}
                            pageLevel={pageLevel}
                            
                        >
                            <InputFormGroup
                                controlId={"document-capture-complete-upload-complete-title-text-input"}
                                automationId={"document-capture-complete-upload-complete-title-text-input"}
                                label={dcuiThemingResources.subTabs.captureComplete.inputs.uploadCompleteTitle.label}
                                ariaLabel={dcuiThemingResources.subTabs.captureComplete.inputs.uploadCompleteTitle.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleUploadCompleteTitleChange}
                                value={dcuiContent.captureComplete.captureUploadCompleteTitle || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.captureComplete.inputs.uploadCompleteTitle.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetUploadCompleteTitle}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.uploadCompleteTitleError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.captureComplete.captureUploadTitle}
                            currentThemingPropertyValue={dcuiContent.captureComplete.captureUploadTitle}
                            pageLevel={pageLevel}
                        >
                            <InputFormGroup
                                controlId={"document-capture-complete-title-text-input"}
                                automationId={"document-capture-complete-title-text-input"}
                                label={dcuiThemingResources.subTabs.captureComplete.inputs.uploadInProgressTitle.label}
                                ariaLabel={dcuiThemingResources.subTabs.captureComplete.inputs.uploadInProgressTitle.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleUploadInProgressTitleChange}
                                value={dcuiContent.captureComplete.captureUploadTitle || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.captureComplete.inputs.uploadInProgressTitle.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetUploadInProgressTitle}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.uploadInProgressTitleError}


                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.captureComplete.captureSuccessMessage}
                            currentThemingPropertyValue={dcuiContent.captureComplete.captureSuccessMessage}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"document-capture-complete-upload-success-message-input"}
                                automationId={"document-capture-complete-upload-success-message-input"}
                                label={dcuiThemingResources.subTabs.captureComplete.inputs.uploadSuccessMessageWithRedirect.label}
                                ariaLabel={dcuiThemingResources.subTabs.captureComplete.inputs.uploadSuccessMessageWithRedirect.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleUploadSuccessMessageWithRedirectChange}
                                value={dcuiContent.captureComplete.captureSuccessMessage || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.captureComplete.inputs.uploadSuccessMessageWithRedirect.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetUploadSuccessMessageWithRedirect}
                            />
                        </ThemingFormGroupWrapper>

                        {validationErrors.uploadSuccessMessageWithRedirectError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.captureComplete.captureSuccessNoRedirectMessage}
                            currentThemingPropertyValue={dcuiContent.captureComplete.captureSuccessNoRedirectMessage}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"document-capture-complete-upload-success-message-without-redirect-input"}
                                automationId={"document-capture-complete-upload-success-message-without-redirect-input"}
                                label={dcuiThemingResources.subTabs.captureComplete.inputs.uploadSuccessMessageWithoutRedirect.label}
                                ariaLabel={dcuiThemingResources.subTabs.captureComplete.inputs.uploadSuccessMessageWithoutRedirect.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleUploadSuccessMessageWithoutRedirectChange}
                                value={dcuiContent.captureComplete.captureSuccessNoRedirectMessage || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.captureComplete.inputs.uploadSuccessMessageWithoutRedirect.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetUploadSuccessMessageWithoutRedirect}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.uploadSuccessMessageWithoutRedirectError}

                        <ThemingFormGroupWrapper
                            inheritedThemingPropertyValue={inheritedDCUIContent.captureComplete.captureUploadErrorMessage}
                            currentThemingPropertyValue={dcuiContent.captureComplete.captureUploadErrorMessage}
                            pageLevel={pageLevel}
                        >
                            <TextareaFormGroup
                                controlId={"document-capture-complete-upload-error-message-input"}
                                automationId={"document-capture-complete-upload-error-message-input"}
                                label={dcuiThemingResources.subTabs.captureComplete.inputs.uploadErrorMessage.label}
                                ariaLabel={dcuiThemingResources.subTabs.captureComplete.inputs.uploadErrorMessage.label}
                                layout={layout}
                                labelWidth={labelWidth}
                                onChange={handleUploadErrorMessageChange}
                                value={dcuiContent.captureComplete.captureUploadErrorMessage || ""}
                                disabled={isDisabled}
                                isRequired={true}
                                afterLabelTooltipText={dcuiThemingResources.subTabs.captureComplete.inputs.uploadErrorMessage.tooltip}
                                afterLabelIcon={isDisabled ? null : faUndo}
                                afterLabelIconOnClick={handleResetUploadErrorMessage}
                            />
                        </ThemingFormGroupWrapper>
                        {validationErrors.uploadErrorMessageError}
                    </ThemingFormContainer>

                    <DCUIThemingPreviewContainer
                        currentTab={DCUIThemingTabTypes.CAPTURE_COMPLETE}
                        dcuiContent={dcuiContent}
                        dcuiTheme={dcuiTheme}
                        documentCaptureSteps={documentCaptureSteps}
                        previewEntityType={previewEntityType}
                    />
                </ThemingConfigurationContainer>
            </ContentPageBody>
        </div>
    );
};
